//@flow
import {ButtonBase, Typography, makeStyles} from '@material-ui/core';
import React, {useState} from 'react';
import CustomModal from '../../../../../../../elements/CustomModal/CustomModal';
import CustomTextField from '../../../../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../../../../assets/styles/colors';
import RelatedResourcesModal, {
  getSingularResourceType,
} from '../../../../../../../elements/RelatedResources/RelatedResourcesModal';
import {useToggle} from '../../../../../../../utils/react';
import {useStoreDomain} from '../../../../../../store/utils';
import {stringToSafeLink} from '../../../../../../../utils/string';
import {
  useBannerCollection,
  getBannerCollectionErrorMessage,
} from '../../../../../utils/banner-collection';

import type {Resource} from '../../../../../../../elements/RelatedResources/RelatedResourcesModal';

import type {BannerCollection} from '../../../../../../../entities';
import {useAlert} from '../../../../../../../elements/Alert/AlertContext';

type CreateCollectionModalProps = {
  open: boolean,
  onClose: () => void,
  onCreateCollection: (collection: BannerCollection) => void,
};

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    maxWidth: '600px',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  title: {
    fontWeight: 600,
    color: colors.grey900,
  },
  sectionTitle: {
    fontWeight: 500,
    color: colors.grey800,
  },
  urlSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  urlSectionInputContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    flexDirection: 'column',
    flexWrap: 'wrap',
    color: colors.grey800,
  },
  urlSectionButton: {
    width: '100%',
    textTransform: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    color: colors.grey500,
    border: `2px dashed ${colors.grey500}`,
    borderRadius: '.75rem',
    paddingBlock: '.85rem',
  },

  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    margin: '1rem 0',
  },
  resourceItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    width: '100%',
    padding: '.5rem',
    borderRadius: '.5rem',
  },
  resourceThumbnail: {
    width: '3rem',
    height: '3rem',
    borderRadius: '.5rem',
    overflow: 'hidden',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  resourceData: {
    flexGrow: 1,
  },
  collectionItemsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    overflow: 'auto',
    maxHeight: '250px',
  },
}));

export default function CreateCollectionModal(
  props: CreateCollectionModalProps
) {
  const {onCreateCollection, onClose, open} = props;

  const {showAlert} = useAlert();
  const classes = useStyles();

  const {createBannerCollection} = useBannerCollection();

  const [modalOpen, toggleModalOpen] = useToggle();
  const [collectionName, setCollectionName] = useState('');
  const [collectionItems, setCollectionItems] = useState<Resource[]>([]);

  const {domain} = useStoreDomain();

  const handleCreateCollection = () => {
    createBannerCollection({
      name: collectionName,
      slug: stringToSafeLink(collectionName),
      storeReferenceEntities: collectionItems.map(item => ({
        // $FlowFixMe
        storeReferenceEntityType: getSingularResourceType(item.type),
        storeReferenceEntityIdentifier: item.id,
      })),
    })
      .then(collection => {
        onCreateCollection(collection);
        setCollectionName('');
        setCollectionItems([]);
        showAlert('Colección creada correctamente', {
          type: 'success',
          duration: 5000,
        });
      })
      .catch(e => {
        showAlert(getBannerCollectionErrorMessage(e?.error?.error), {
          type: 'error',
          duration: 5000,
        });
      });
  };

  return (
    <CustomModal open={open} handleCloseModal={onClose}>
      <aside className={classes.root}>
        <Typography className={classes.title}>Crear Colección</Typography>
        <div className={classes.urlSection}>
          <Typography className={classes.sectionTitle}>
            1. Dale el nombre a la colección. p. ej, Verano 2023
          </Typography>
          <div className={classes.urlSectionInputContainer}>
            <CustomTextField
              label="Nombre de la colección"
              value={collectionName}
              onChange={event => {
                setCollectionName(event.target.value);
              }}
            />
            <Typography className={classes.urlSectionInputUrl}>
              {domain.domainName}/collection/{`<Identificador>`}/
              {stringToSafeLink(collectionName)}
            </Typography>
          </div>
        </div>

        <div className={classes.urlSection}>
          <Typography className={classes.sectionTitle}>
            2. Selecciona productos o categorías para agregar a la colección
          </Typography>
          <ButtonBase
            className={classes.urlSectionButton}
            onClick={toggleModalOpen}
          >
            <Typography className={classes.sectionTitle}>
              Agregar productos o categorías
            </Typography>
          </ButtonBase>
          <section className={classes.collectionItemsContainer}>
            {collectionItems.map((resource: Resource, index) => (
              <div
                className={classes.resourceItem}
                key={`${resource.id}-${resource.thumbnail || index}`}
              >
                {resource.thumbnail && (
                  <picture className={classes.resourceThumbnail}>
                    <img src={resource.thumbnail} alt="" />
                  </picture>
                )}

                <div className={classes.resourceData}>
                  <Typography>{resource.label}</Typography>
                </div>

                {/*   <div className={classes.resourceActions}>
                  <CustomButton>Eliminar</CustomButton>
                </div> */}
              </div>
            ))}
          </section>
        </div>

        <footer className={classes.footer}>
          <CustomButton
            color="primary"
            variant="contained"
            onClick={handleCreateCollection}
          >
            Crear Colección
          </CustomButton>
        </footer>
      </aside>

      <RelatedResourcesModal
        availableResourceType={['CATEGORIES', 'PRODUCTS']}
        onClose={() => {
          toggleModalOpen();
        }}
        limitResources={15}
        open={modalOpen}
        onSubmit={value => {
          setCollectionItems(value.resources);
          toggleModalOpen();
        }}
      />
    </CustomModal>
  );
}
